import { useState, useEffect } from "react";
import ChatPromptInput from "src/reusable/Chat/PromptInput/ChatPromptInput";
import { FileUploadState } from "src/reusable/types/fileUploadWithProgress";
import { NucToast } from "src/reusable/components/Toast";
import { useChatStore } from "src/views/assistant/stores/ChatStore";
import { ImageContent } from "src/views/assistant/types";
import { type TokenUsageData } from "src/reusable/Chat/TokenUsageGauge";

type VnvAssistChatInputProps = {
  chatStreamError: string | null;
  isChatConnectionError: boolean | null;
  isChatConnected: boolean;
  isChatLoading: boolean;
  isChatEnded: boolean;
  terminateConnection: () => void;
  sendUserMessage: (text: string, images?: ImageContent[]) => Promise<boolean>;
  tokenUsage?: TokenUsageData | null;
};

const VnvAssistChatInput = ({
  chatStreamError,
  isChatConnectionError,
  isChatConnected,
  isChatLoading,
  isChatEnded,
  terminateConnection,
  sendUserMessage,
  tokenUsage,
}: VnvAssistChatInputProps) => {
  const [userInput, setUserInput] = useState("");
  const { uploadedFiles, addUploadedFile, removeUploadedFile } = useChatStore();

  const handleFilesChange = (files: FileUploadState[]) => {
    uploadedFiles.forEach((file: any) => {
      if (!file.isFromDialogueHistory && !file.isFromProject) {
        removeUploadedFile(file.id);
      }
    });
    files.forEach((file) => addUploadedFile(file));
  };

  const handleSubmit = async () => {
    if (isChatConnected || isChatLoading) return;
    if (userInput.trim() === "" && uploadedFiles.length === 0) {
      NucToast({ message: "Please enter a message", type: "error" });
      return;
    }
    const ok = await sendUserMessage(userInput);
    if (ok) setUserInput("");
  };

  useEffect(() => {
    if (chatStreamError || isChatConnectionError) terminateConnection();
  }, [chatStreamError, isChatConnectionError, terminateConnection]);

  return (
    <ChatPromptInput
      onSubmit={handleSubmit}
      isInputting={!isChatConnected && userInput !== ""}
      isChatLoading={isChatLoading}
      isGenerating={!isChatLoading && !isChatEnded}
      onStopChat={terminateConnection}
      value={userInput}
      onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
        setUserInput(e.target.value)
      }
      onFilesChange={handleFilesChange}
      placeholder="Ask about the document or validation results…"
      product="vnv-assist"
      tokenUsage={tokenUsage}
    />
  );
};

export default VnvAssistChatInput;
